<template>
  <el-scrollbar :ref="scrollbarRef">
    <div class="full">
      <!-- Search Toolbar -->
      <el-row class="mr-b10" type="flex" justify="space-between">
        <w-log-search-field :field-options="fieldOptions" :search-keywords="searchHistory"
          @add-search-tag="addSearchTag" @delete-hist-by-index="deleteByIndex" @delete-all-history="deleteAllHistory" />
        <div align="right">
          <el-button size="small" @click="exportExcel"><img src="@/assets/img/icons/excel.png" style="position:relative;top:3px;left:-2px"> 엑셀출력</el-button>
        </div>
      </el-row>
      <hr class="mr-b10" />
      <el-row class="mr-b30 vertical-center-box">
        <span>
          <el-tag class="label mr-r10">태그</el-tag>
        </span>
        <span v-if="!isEmpty(searchTags)">
          <el-tag class="mr-r4" v-for="(tag, index) in searchTags" :key="`${tag.field}_${tag.value}`" :type="tag.regex === 'Y' ? 'success' : null" closable @close="deleteSearchTag(index)">
            {{tag.label}}
          </el-tag>
        </span>
      </el-row>
      <!-- Histogram Chart -->
      <el-row type="flex" :class="getHistogramClass" justify="space-between">
        <div class="title-bar title-h4">
          <span class="c-pointer" @click="onFoldHistogram" v-show="!histogramFoldFlag"><i class="far fa-angle-down mr-r4" /></span>
          <span class="c-pointer" @click="onUnfoldHistogram" v-show="histogramFoldFlag"><i class="far fa-angle-right mr-r4" /></span>
          히스토그램
        </div>
        <div align="right" v-show="!histogramFoldFlag"><el-button @click="onClickRefreshChart"><i class="fas fa-sync-alt"></i></el-button></div>
      </el-row>
      <div class="mr-b30" v-show="!histogramFoldFlag" v-loading="chartLoading">
        <highstock ref="highstock" class="chart" :options="options"></highstock>
      </div>
      <!-- Log Search Grid -->
      <div class="title-bar title-h4">
        로그 목록
      </div>
      <el-table-wrapper v-infinite-scroll="loadScrollData" ref="logTable" border :fit="true" :total="total" :column-default="columnDefault" :columns="columns" :data="logDatas" :pagination="false" highlight-current-row
        @cell-click="getSelection">
        <template slot-scope="scope" slot="log-detail">
          <span class="c-pointer" @click="openDetailPopup(scope.row)">
            <i class="far fa-search-plus"/>
          </span>
        </template>
      </el-table-wrapper>
      <!-- Popup & Popover -->
      <w-log-detail-popup ref="logDetailPopup"></w-log-detail-popup>
      <el-popover ref="miniPopup" v-model="showAddTag" @blur="closeTagPopup" align="center"
      :style="showAddTag ? { top: `${tagPopupPosition.top}px`, left: `${tagPopupPosition.left}px`, position: 'absolute',
      height: '50px', width: '300px' } : { display: 'none' }">
        <el-row type="flex" justify="space-between">
          <div class="vertical-center-box mr-r8" style="word-break: 'break-all'">{{selectedText.value}}</div>
          <div class="vertical-center-box" align="right">
            <el-button type="primary" @click="onAddSearchButton" class="mr-r4">검색</el-button>
            <el-button @click="closeTagPopup">취소</el-button>
          </div>
        </el-row>
        <span v-show="checkDuplicate" style="color: #E02323; margin-top: 10px; font-size: 0.75rem;">이미 태그에 추가된 검색어입니다.</span>
      </el-popover>
    </div>
    <w-scroll-top-button :visible="enableScollTop" @move-scroll-top="moveScrollTop"/>
  </el-scrollbar>
</template>

<script>
import { mapGetters } from 'vuex';
import { isEmpty, isNil, findIndex, cloneDeep, forEach, find } from 'lodash';
import Cookies from 'js-cookie';

import { EMPTY_TEXT } from '@/utils/string';
import { getDateToString, getIntervalDatetimeParams } from '@/utils/date';
import msgUtils from '@/utils/message';
import { convertValueByUnitData } from '@/utils/number';
import { columnDefault } from '@/constants/table';
import { chartColorSet } from '@/constants/chart-options';
import LogAPI from '@/api/log/integrated-log';
import FileAPI from '@/api/common/file';
import ScrollbarMixin from '@/mixins/scrollbar';

import WLogSearchField from '@/views/log-analysis/components/LogSearchField';
import WLogDetailPopup from '../popup/LogDetailPopup';

const ALERT_COLOR = '#Fb4e7c';

export default {
  name: 'WAnalysis',
  mixins: [ScrollbarMixin],
  components: {
    WLogDetailPopup,
    WLogSearchField,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      columns: [],
      columnDefault,
      limit: 50,
      start: 0,
      total: 0,
      logDatas: [],
      options: {},
      tagPopupPosition: { // 검색어 추가 팝업의 위치
        top: 0,
        left: 0,
      },
      selectedText: {
        field: 'message',
        value: '',
        label: '',
        regex: 'N',
      },
      searchTags: [],
      keywords: [],
      searchHistory: [],
      histogramFoldFlag: false,
      fieldOptions: [],
      fieldInfos: [],
      showAddTag: false,
      checkDuplicate: false,
      chartLoading: false,
      navStartDatetime: null,
      navEndDatetime: null,
      scrolling: false,
      scrollListener: null,
      virtualScroll: {
        scrollHeight: 0,
        rowHeight: 29,
        offset: 40,
        showItems: [],
        topBufferHeight: 0,
        bottomBufferHeight: 0,
      },
      prevSelectedNode: null,
    };
  },
  computed: {
    ...mapGetters(['selectedNode', 'analysisDateParams']),
    getHistogramClass() {
      if (this.histogramFoldFlag) {
        // 접혀있을 때
        return 'vertical-center-box mr-b30';
      }
      return 'vertical-center-box';
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(value) {
        this.searchTags = [];
        this.navStartDatetime = null;
        this.navEndDatetime = null;
        if (value) {
          this.loadData();
          this.loadDefaultShowFields(this.selectedNode);
        }
      },
    },
    fieldOptions(value) {
      this.searchTags = [];
      this.columns = [];
      this.$nextTick(() => {
        if (!isEmpty(value)) {
          forEach(value, (item) => {
            this.columns.push({
              label: item.fieldName,
              prop: item.fieldId,
              width: this.getColumnWidth(item.fieldId),
              minWidth: 100,
              formatter: item.fieldId === 'timestamp' ? this.getDateFormatter : this.getStringFormatter,
              showOverflowTooltip: item.fieldId !== 'originMessage',
              renderHeader: this.appendHeaderTooltip,
            });
          });
          this.columns.push({
            label: '상세보기',
            width: 70,
            scopedSlot: 'log-detail',
            align: 'center',
          });
        }
      });
    },
    /**
     * 쿠키에 저장된 검색 이력 키워드를 조회해 검색 이력 팝오버 그리드 형식으로 가공함
     * @type {Object}
     */
    keywords: {
      immediate: true,
      deep: true,
      handler(item) {
        const keywords = cloneDeep(item);
        if (!isEmpty(keywords) && !isEmpty(this.fieldInfos)) {
          const keywordsLength = keywords.length;
          item.forEach((keyword, index) => {
            keywords[keywordsLength - index - 1] = keyword;
          });
          this.searchHistory = keywords;
        } else {
          this.searchHistory = null;
        }
      },
    },
    selectedNode: {
      immediate: true,
      handler(value) {
        this.navStartDatetime = null;
        this.navEndDatetime = null;
        this.searchTags = [];
        if (!isEmpty(value) && this.active && !isEmpty(this.analysisDateParams)) {
          this.$nextTick(() => {
            this.loadData();
            this.loadDefaultShowFields();
          });
        }
      },
    },
    analysisDateParams: {
      immediate: true,
      handler(value) {
        this.navStartDatetime = null;
        this.navEndDatetime = null;
        if (!isEmpty(value) && this.active && !isEmpty(this.selectedNode)) {
          this.loadData();
        }
      },
    },
    searchTags: {
      deep: true,
      handler(value) {
        this.$emit('change-search-tags', value);
      },
    },
  },
  mounted() {
    LogAPI.getLogAllFields().then((response) => {
      if (response.data.success) {
        this.fieldInfos = response.data.data;
      } else {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '필드 정보', false));
      }
    }).catch((e) => {
      this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '필드 정보', false));
      throw new Error(e);
    });
    this.setInitChartOptions();
    this.setScrollData();
  },
  methods: {
    setInitChartOptions() {
      this.options = {
        chart: {
          backgroundColor: 'transparent',
          spacingTop: 5,
          spacingBottom: 0,
          zoomType: 'x',
          height: 200,
        },
        title: false,
        credits: false,
        xAxis: {
          type: 'datetime',
          minRange: 5 * 60 * 1000,
          labels: {
            format: '{value: %Y-%m-%d %H:%M}',
            style: {
              color: '$chinagray',
              fontSize: '9px',
            },
          },
          lineWidth: 0,
          events: {
            afterSetExtremes: this.onNavigatorMove,
          },
        },
        yAxis: {
          opposite: false,
          minTickInterval: 0,
          startOnTick: false,
        },
        tooltip: {
          xDateFormat: '%Y-%m-%d %H:%M:%S',
          split: false,
          shared: true,
          valueDecimals: 0,
          valueSuffix: '개',
          formatter() {
            let tooltip = getDateToString(this.x, 'YYYY-MM-DD HH:mm:ss');
            this.points.forEach((point) => {
              let yValue = convertValueByUnitData(point.y, 'count', '개', 1, null, null, true);
              if (point.series.userOptions.type === 'areasplinerange') {
                yValue = `${convertValueByUnitData(point.point.low, 'count', '개', 1, null, null, true)} - ${convertValueByUnitData(point.point.high, 'count', '개', 1, null, null, true)}`;
              }
              tooltip += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${yValue}</b><br/>`;
            });
            return tooltip;
          },
        },
        rangeSelector: false,
        scrollbar: false,
        plotOptions: {
          series: {
            animation: false,
            lineWidth: 1.8,
            marker: {
              radius: 2,
            },
          },
          column: {
            maxPointWidth: 10,
          },
        },
        series: [],
        colors: chartColorSet,
      };
    },
    isEmpty,
    /**
     * 그리드와 검색 필드에 보여줄 기본 필드 목록을 조회해온다. (show_flag = 'Y')
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadDefaultShowFields() {
      // 선택한 트리 노드가 dataindex일 경우 : dataindex에 할당된 필드를 추가로 조회함
      if (this.selectedNode.type === 'dataindex') {
        const params = {
          params: {
            dataindexId: this.selectedNode.dataindexId,
          },
        };
        LogAPI.getLogFieldsByIndex(params).then((response) => {
          if (response.data.success) {
            const fieldOptions = response.data.data;
            this.fieldOptions = fieldOptions;
            this.initSearchHistory();
          } else {
            this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '필드 정보', false));
          }
        }).catch((e) => {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '필드 정보', false));
          throw new Error(e);
        });
      } else {
        LogAPI.getLogShowFields().then((response) => {
          if (response.data.success) {
            this.fieldOptions = response.data.data;
            this.initSearchHistory();
          } else {
            this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '필드 정보', false));
          }
        }).catch((e) => {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '필드 정보', false));
          throw new Error(e);
        });
      }
    },
    loadData() {
      const params = this.setParameter();
      this.loadChartData(params);
      this.loadGridData(params);
    },
    /**
     * 히스토그램 차트의 로그 집계 데이터와 베이스라인 차트 데이터를 조회해온다.
     * @author Minyeong
     * @param  {[type]} params [description]
     * @return {[type]}        [description]
     */
    loadChartData(params) {
      this.setInitChartOptions();
      this.options.series = [];
      let baselineChartDatas;
      const zones = [];

      this.chartLoading = true;
      // ES에서 수집된 로그를 시간별 추이 aggs(로그 집계) 데이터로 조회
      LogAPI.getAnalysisHistogramData(params).then((response) => {
        if (response.data.success) {
          const series = response.data.data;
          series[0].type = 'line';
          if (!isEmpty(this.selectedNode) && this.selectedNode.type === 'filter' && isEmpty(this.searchTags)) {
            let baselineParams = {
              params: {
                filterId: this.selectedNode.customFilterId,
              },
            };
            // 베이스라인 정보 조회(stats_period, upper_bound, lower_bound)
            LogAPI.getBaselineInfo(baselineParams).then((infoResponse) => {
              if (infoResponse.data.success) {
                const baselineInfo = infoResponse.data.data;
                baselineParams = {
                  customFilterId: this.selectedNode.customFilterId,
                  startDateTime: this.analysisDateParams.startDatetime,
                  endDateTime: this.analysisDateParams.endDatetime,
                  checkDatetime: getIntervalDatetimeParams(new Date(), 'month', baselineInfo.statsPeriod),
                };

                const DAY30 = 1000 * 3600 * 24 * 30;
                const gap = new Date(this.analysisDateParams.endDatetime).getTime() - new Date(this.analysisDateParams.startDatetime).getTime();

                /**
                 * 베이스라인 데이터(H, D)를 조회해 현재 조회 중인 히스토그램 차트 데이터와 동기화하는 로직
                 */
                LogAPI.getBaselineChartData(baselineParams).then((res) => {
                  if (res.data.success && !isEmpty(res.data.data)) {
                    const baselineData = res.data.data;
                    const aggData = series[0].data;
                    const baselineChartData = [];
                    const aggs = [];
                    forEach(aggData, (item, index) => {
                      const chartX = new Date(item[0]); // 차트 데이터의 X축값(datetime)
                      let avg;

                      // 조회 기간이 30일보다 클 경우 1일 기준 베이스라인 데이터를 조회하고 30일 이하일 경우 1시간 기준 베이스라인 데이터를 조회함
                      if (gap > DAY30) {
                        // 베이스라인 데이터에서 실제 집계 데이터의 timestamp와 일치하는 값을 찾음
                        // ex. 집계 데이터의 timestamp가 2019-01-23일 경우, 1일 베이스라인 데이터에서 '23'에 해당하는 평균 logCount값을 조회
                        avg = find(baselineData, { checkDatetime: chartX.getDate() });
                      } else {
                        // ex. 집계 데이터의 timestamp가 2019-01-23 16:00일 경우, 1시간 베이스라인 데이터에서 '16'에 해당하는 평균 logCount값을 조회
                        avg = find(baselineData, { checkDatetime: chartX.getHours() });
                      }

                      // 조회해온 베이스라인 데이터의 logCount값을 가공해 차트에 뿌려주기 위해 divider값(number)을 가져옴
                      const valueDivider = this.getValueDividerByTimeInterval(gap);
                      if (!isEmpty(avg)) {
                        // 베이스라인 정보의 upperBound(상한)과 lowerBound(하한)의 값, divider로 나눈 logCount로 베이스라인의 상한값과 하한값을 계산
                        const upperBound = (avg.logCount / valueDivider) * (1 + (baselineInfo.upperBound / 100));
                        const lowerBound = (avg.logCount / valueDivider) * (1 - (baselineInfo.lowerBound / 100));
                        baselineChartData.push({ x: item[0], low: Number(lowerBound.toFixed(0)), high: Number(upperBound.toFixed(0)) });
                        if (item[1] > upperBound || item[1] < lowerBound) {
                          aggs.push({ x: item[0], y: item[1], color: ALERT_COLOR });
                          // 로그 데이터의 수가 상한보다 높을 경우 or 하한보다 낮을 경우 > 차트의 색상을 ALERT_COLOR로 변경
                          // highcharts series 옵션의 zones를 사용(zoneAxis: x)
                          if (index === 0) {
                            zones.push({ value: item[0], color: ALERT_COLOR });
                          } else if (index !== 0 && aggs[index - 1].color !== ALERT_COLOR) {
                            zones.push({ value: item[0] });
                          } else if (index === aggData.length - 1 && aggs[index - 1].color === ALERT_COLOR) {
                            zones.push({ value: item[0], color: ALERT_COLOR });
                          }
                        } else {
                          aggs.push({ x: item[0], y: item[1] });
                          if (index === 0) {
                            zones.push({ value: item[0] });
                          } else if (index !== 0 && aggs[index - 1].color === ALERT_COLOR) {
                            zones.push({ value: item[0], color: ALERT_COLOR });
                          } else if (index === aggData.length - 1 && aggs[index - 1].color === ALERT_COLOR) {
                            zones.push({ value: item[0], color: ALERT_COLOR });
                          }
                        }
                      } else {
                        aggs.push({ x: item[0], y: item[1] });
                        if (index === 0) {
                          zones.push({ value: item[0] });
                        } else if (index !== 0 && aggs[index - 1].color === ALERT_COLOR) {
                          zones.push({ value: item[0], color: ALERT_COLOR });
                        } else if (index === aggData.length - 1 && aggs[index - 1].color === ALERT_COLOR) {
                          zones.push({ value: item[0], color: ALERT_COLOR });
                        }
                      }
                    });
                    baselineChartDatas = {
                      name: '하한 - 상한',
                      lineWidth: 0,
                      data: baselineChartData,
                      type: 'areasplinerange',
                      fillOpacity: 0.3,
                      zIndex: 0,
                      marker: {
                        enabled: false,
                      },
                    };
                    this.options.series = [{ name: '수집 로그 수', data: aggs, type: 'line', zoneAxis: 'x', zones }, baselineChartDatas];
                  } else {
                    this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '베이스라인 차트', false));
                  }
                }).catch((e) => {
                  this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '베이스라인 차트', false));
                  throw new Error(e);
                });
              } else {
                this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '베이스라인 정보', false));
              }
            }).catch((e) => {
              this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '베이스라인 정보', false));
              throw new Error(e);
            });
          } else {
            this.options.series = series;
          }
          this.chartLoading = false;
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '로그 이력 차트', false));
        throw new Error(e);
      }).finally(() => {
        this.chartLoading = false;
      });
    },
    /**
     * 히스토그램 차트 하단 그리드의 로그 목록 데이터를 조회해온다.
     * @author Minyeong
     * @param  {[type]} params [description]
     * @return {[type]}        [description]
     */
    loadGridData(params) {
      LogAPI.getLogDatas(params).then((response) => {
        if (response.data.success) {
          this.logDatas = response.data.data;
          this.total = response.data.total;
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '로그', false));
        throw new Error(e);
      });
    },
    /**
     * 현재 검색 조건(트리 노드, 태그 검색어, 조회 기간)에서 로그와 차트 데이터를 조회해온다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    setParameter() {
      let params;
      if (this.selectedNode.type === 'dataindex') {
        params = this.getIndexParams();
      } else if (this.selectedNode.type === 'filter') {
        params = this.getFilterParams();
      } else if (this.selectedNode.type === 'group') {
        params = {
          type: 'group',
          filterGroupId: this.selectedNode.filterGroupId,
        };
      } else {
        params = {
          type: 'root',
        };
      }
      const queryList = this.setQueryList();
      params = {
        ...params,
        queryList,
        startDateTime: !isNil(this.navStartDatetime) ? this.navStartDatetime : this.analysisDateParams.startDatetime,
        endDateTime: !isNil(this.navEndDatetime) ? this.navEndDatetime : this.analysisDateParams.endDatetime,
        start: this.start,
        limit: this.limit,
      };

      return params;
    },
    /**
     * 저장소 트리 노드를 선택했을 때 조회용 parameter를 설정해준다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    getIndexParams() {
      const params = { type: this.selectedNode.type, dataindexId: this.selectedNode.dataindexId };
      return params;
    },
    /**
     * 필터 노드를 선택했을 때 조회용 parameter를 설정해준다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    getFilterParams() {
      const params = { type: this.selectedNode.type, customFilterId: this.selectedNode.customFilterId };
      return params;
    },
    /**
     * 실제 Aggs로 조회해온 집계 데이터들의 timestamp 간격을 계산해 베이스라인 logCount를 나눌 값을 구한다.
     * ex. time interval이 5분 단위일 때, 1시간 안에 12개의 포인트가 찍힘 > 베이스라인 logCount는 최소 1시간 단위로 집계되므로 5분 단위의 평균을 구하려면 12로 나눠야함
     * ex. gap이 30일 초과일 때 베이스라인 정보를 1일 단위로 가져오기 때문에 이를 실제 aggs 데이터의 timestamp와 동기화해야 함(2시간 단위 차트의 경우 > 12로 나눠야함)
     * @author Minyeong
     * @param  {[type]} gap [endDatetime.getTime() - startDatetime.getTime()]
     * @return {[type]}     [description]
     */
    getValueDividerByTimeInterval(gap) {
      const DAY3 = 1000 * 60 * 60 * 24 * 3;
      const DAY19 = 1000 * 60 * 60 * 24 * 19;
      const DAY30 = 1000 * 60 * 60 * 24 * 30;
      const DAY90 = 1000 * 60 * 60 * 24 * 90;

      if (gap >= 0 && gap <= DAY3) {
        // gap이 0일 이상 3일 이하일 때 > 조회해오는 로그 집계 데이터의 time interval이 5분이므로 베이스라인 H 데이터의 logCount를 12로 나눔(60/12 = 5)
        return 12;
      } else if (gap > DAY3 && gap <= DAY19) {
        // gap이 3일 이상 19일 이하일 때 > 조회해오는 로그 집계 데이터의 time interval이 30분이므로 베이스라인 H 데이터의 logCount를 2로 나눔(60/2 = 30)
        return 2;
      } else if (gap > DAY19 && gap <= DAY30) {
        // gap이 19일 이상 30일 이하일 때 > 조회해오는 로그 집계 데이터의 time interval이 2시간이므로 베이스라인 H 데이터의 logCount에 2를 곱함(60*2 = 120)
        return 0.5;
      } else if (gap > DAY30 && gap <= DAY90) {
        // gap이 30일 이상 90일 이하일 때 > 조회해오는 로그 집계 데이터의 time interval이 2시간이므로 베이스라인 D 데이터의 logCount를 12로 나눔(24/12 = 2)
        return 12;
      } else if (gap > DAY90) {
        // gap이 90일 이상일 때 > 조회해오는 로그 집계 데이터의 time interval이 1일이므로 베이스라인 D 데이터의 logCount를 그대로 사용함
        return 1;
      }

      return 1;
    },
    /**
     * 태그 검색어 및 헤더 검색어를 queryList 데이터로 가공
     * @author Minyeong
     * @param  {[type]} queryList [description]
     */
    setQueryList() {
      const queryList = [];
      forEach(this.searchTags, (item) => {
        queryList.push({ field: item.field, value: item.value, operator: 'AND', regex: item.regex });
      });
      return queryList;
    },
    /**
     * 히스토그램 하단의 차트 Navigator를 움직였을 때 발생하는 이벤트 함수 (startDatetime, endDatetime을 navigator의 값으로 설정해 로그 데이터를 조회)
     * @author Minyeong
     * @param  {[type]} event [description]
     * @return {[type]}       [description]
     */
    onNavigatorMove(event) {
      // 차트 최초 로딩 시 설정된 navigator 범위로 로그 데이터가 조회되는 문제 > navigator가 trigger 되었을 때에만 navigator 시간 범위로 조회하도록 수정
      if (!isNil(event.trigger)) {
        // navigator를 조정하는데 max 값 또는 min 값으로 조회할 경우 navigator값이 아니라 analysisDateParams의 시간 범위로 조회하도록 함
        // 차트에 나타난 로그 개수와 실 조회된 로그 개수의 차이를 없애기 위함
        const isMax = event.max === event.dataMax;
        const isMin = event.min === event.dataMin;
        this.start = 0;

        this.navStartDatetime = Math.floor(event.min);
        this.navEndDatetime = Math.floor(event.max);
        if (isMax) {
          this.navEndDatetime = null;
        }
        if (isMin) {
          this.navStartDatetime = null;
        }
      }
      const params = this.setParameter();
      this.loadGridData(params);
    },
    /**
     * 드래그 or 더블클릭으로 그리드 내부 텍스트 영역 선택 시 태그 검색 팝업 활성화(cell-click 이벤트)
     * @author Minyeong
     * @param  {Object} row    그리드 Row 객체
     * @param  {Object} column 그리드 Column 객체
     * @return {[type]}        [description]
     */
    getSelection(row, column, cell, event) {
      event.stopPropagation();
      // 수집시각은 상단 Datepicker에서 설정할 수 있으므로 검색 제한
      if (column.label === '수집시각' || column.label === '상세보기') {
        return;
      }
      this.checkDuplicate = false; // 이미 검색어로 태그에 추가되었는지 확인하는 Flag
      const selected = window.getSelection(); // 사용자가 선택한 영역 정보
      const selectedText = selected.toString(); // 영역 정보로부터 사용자가 선택한 텍스트 읽어오기

      if (selectedText && selected.anchorNode === selected.extentNode) {
        // 선택된 텍스트가 있을 때 : 태그 추가용 팝업 ON
        // top, left : 사용자가 선택한 영역의 좌표 위치. 태그 검색 팝업의 위치를 잡아주기 위해 설정
        const top = selected.getRangeAt(0).getBoundingClientRect().top;
        const left = selected.getRangeAt(0).getBoundingClientRect().left;
        this.tagPopupPosition.top = top - 170;
        this.tagPopupPosition.left = left - 260;

        // 태그 검색 팝업 활성화
        this.showAddTag = true;

        // 태그에 노출시킬 라벨(컬럼명 : 값)과 컬럼속성명, 값을 저장
        this.selectedText.label = `${column.label} : ${selectedText}`;
        this.selectedText.field = column.property;
        this.selectedText.value = selectedText;
        this.selectedText.regex = 'N';
      } else {
        // 없을 때에는 팝업 띄우지 않음
        this.closeTagPopup();
      }
    },
    /**
     * 그리드 선택 검색 시 검색어를 태그 검색어에 추가한다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    onAddSearchButton() {
      this.addSearchTag(this.selectedText);
    },
    /**
     * 태그 검색어를 추가하고 쿠키에 검색 이력을 남긴다.
     * @author Minyeong
     * @param  {[type]} selectedText [description]
     */
    addSearchTag(selectedText) {
      this.searchTags.push(selectedText);
      this.loadData();
      this.setSearchHistory(selectedText);
      this.closeTagPopup();
    },
    closeTagPopup() {
      this.selectedText = {
        field: 'message',
      }; // 선택된 텍스트 초기화
      this.showAddTag = false;
    },
    deleteSearchTag(index) {
      this.searchTags.splice(index, 1);
      this.loadData();
    },
    openDetailPopup(log) {
      this.$refs.logDetailPopup.open(log);
    },
    /**
     * 로그 그리드의 데이터를 엑셀로 출력함
     * @author Minyeong
     * @return {[type]} [description]
     */
    exportExcel() {
      const params = this.setParameter();
      params.start = Math.floor(this.total / 10000) * 10000;
      params.limit = 10000;
      params.excelFlag = true;
      LogAPI.getLogAnalysisExcelName(params).then((response) => {
        if (response.data.success && !isEmpty(response.data.data)) {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.OUTPUT, '엑셀 파일', true));
          FileAPI.downloadExportedExcelFile(response.data.data);
        } else {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.OUTPUT, '엑셀 파일', false));
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.OUTPUT, '엑셀 파일', false));
        throw new Error(e);
      });
    },
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(getIntervalDatetimeParams(value, 'hour', 9), 'YYYY-MM-DD HH:mm:ss');
      }
      return EMPTY_TEXT;
    },
    getStringFormatter(row, column, value) {
      if (!isNil(value)) {
        return value;
      }
      return EMPTY_TEXT;
    },
    /**
     * 검색 이력(쿠키)을 초기화
     * @author Minyeong
     * @return {[type]} [description]
     */
    initSearchHistory() {
      if (!isEmpty(Cookies.getJSON('searchHistory'))) {
        this.keywords = Cookies.getJSON('searchHistory').keywords;
      }
    },
    /**
     * 그리드 선택 검색/텍스트 입력 검색 시 태그에 추가된 값을 검색 이력(쿠키)에 저장한다.
     * @author Minyeong
     * @param  {[type]} value [description]
     */
    setSearchHistory(value) {
      const maxKeywordLength = 10;
      const searchHistory = Cookies.getJSON('searchHistory');
      const searchKeyword = value.value;
      if (isEmpty(searchHistory) || isEmpty(searchHistory.keywords)) {
        const history = {
          keywords: [searchKeyword],
        };
        Cookies.set('searchHistory', history);
      } else {
        const keywords = searchHistory.keywords;
        if (keywords.length === maxKeywordLength) {
          keywords.splice(0, 1);
        }
        // 이미 검색 이력에 존재하는 키워드인지 확인 >> 존재하는 키워드라면 기존 것을 삭제 후 새로 push
        const index = findIndex(keywords, (item) => searchKeyword === item);
        if (index !== -1) {
          keywords.splice(index, 1);
        }
        keywords.push(searchKeyword);
        const history = {
          keywords,
        };
        Cookies.set('searchHistory', history);
      }
      this.keywords = Cookies.getJSON('searchHistory').keywords;
    },
    /**
     * 모든 검색 이력을 삭제
     * @author Minyeong
     * @return {[type]} [description]
     */
    deleteAllHistory() {
      Cookies.set('searchHistory', null);
      this.keywords = null;
    },
    /**
     * 선택한 단일 검색 이력을 삭제
     * @author Minyeong
     * @param  {[type]} index [description]
     * @return {[type]}       [description]
     */
    deleteByIndex(index) {
      const keywords = Cookies.getJSON('searchHistory').keywords;
      const keywordsLength = keywords.length;
      keywords.splice(keywordsLength - index - 1, 1);
      Cookies.set('searchHistory', { keywords });
      this.keywords = keywords;
    },
    /**
     * 동적 컬럼의 Width를 설정한다.
     * @author Minyeong
     * @param  {[type]} fieldId [description]
     * @return {[type]}         [description]
     */
    getColumnWidth(fieldId) {
      if (fieldId === 'timestamp') {
        return 150;
      } else if (fieldId === 'originMessage') {
        return 700;
      }
      return null;
    },
    onClickRefreshChart() {
      this.navStartDatetime = null;
      this.navEndDatetime = null;
      const params = this.setParameter();
      params.startDateTime = this.analysisDateParams.startDatetime;
      params.endDateTime = this.analysisDateParams.endDatetime;
      this.loadChartData(params);
      this.loadGridData(params);
    },
    onFoldHistogram() {
      this.histogramFoldFlag = true;
    },
    onUnfoldHistogram() {
      this.histogramFoldFlag = false;
    },
    /**
     * Table Header Overflow Tooltip : 컬럼의 가로길이보다 텍스트의 가로길이가 길어 ...으로 축약되는 경우 헤더에도 툴팁을 달아준다.
     * @author Minyeong
     * @param  {[type]} h    [description]
     * @param  {[type]} data [description]
     * @return {[type]}      [description]
     */
    appendHeaderTooltip(h, data) {
      const text = document.createElement('canvas');
      const ctx = text.getContext('2d');
      // el-table에서 사용하고 있는 폰트 사이즈 및 폰트 종류
      ctx.font = '0.75rem NunitoSans';
      const textWidth = ctx.measureText(data.column.label).width;
      // 80 : 컬럼의 minWidth인 100을 기준으로 텍스트가 차지할 수 있는 최대 영역의 width
      const maxTextWidth = 80;
      return h('el-tooltip', { attrs: { content: data.column.label, effect: 'dark', placement: 'top', trigger: 'hover', disabled: textWidth < maxTextWidth } }, [
        data.column.label,
        h(
          'span',
          {
            attrs: {
              slot: 'reference',
            },
          },
          [data.column.label],
        ),
      ]);
    },
    loadScrollData() {
      if (!this.scrolling && !isEmpty(this.logDatas)) {
        const params = this.setParameter();
        if (!isEmpty(this.logDatas)) {
          params.searchAfter = this.logDatas[this.logDatas.length - 1].searchAfter;
        }
        this.scrolling = true;
        LogAPI.getLogDatas(params).then((response) => {
          if (response.data.success && !isEmpty(response.data.data)) {
            const logDatas = this.logDatas;
            logDatas.push.apply(this.logDatas, response.data.data);
            this.total = response.data.total;
          }
        }).catch((e) => {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '로그', false));
          throw new Error(e);
        }).finally(() => {
          this.scrolling = false;
        });
      }
    },
  },
};
</script>

<style scoped>
.search-list {
  height: 30px;
  width: 100%;
}
.search-list:hover {
  background-color: #FAECC5;
}
</style>



// WEBPACK FOOTER //
// src/views/log-analysis/Analysis/index.vue